/**
 * Add-to-home-screen platform / scenario detection (pure, user-agent driven).
 * 「保存到桌面」平台与场景检测（纯函数，只看 user agent）。
 *
 * Every judgement function takes the user agent as an ARGUMENT and never reads
 * `navigator` itself. That is what makes the whole ruleset unit-testable
 * against a catalogue of real UA strings. The only navigator-reading helpers
 * are the two thin wrappers at the bottom of the file; they gather inputs and
 * delegate, and they tolerate a missing `navigator` / `window` (SSR, jsdom).
 *
 * 所有判定函数都把 UA 当参数传进来，绝不自己读 navigator —— 这样整套规则才能
 * 用真实 UA 样本做单测。文件底部两个薄包装是唯一读 navigator 的地方，只负责
 * 取值再委托给纯函数，且容忍 navigator / window 缺失（SSR、jsdom）。
 *
 * Rule of thumb: assert on TOKENS, never on version numbers. Android's reduced
 * UA is frozen at "Android 10; K", iOS 26 Safari still reports "iPhone OS
 * 18_6", and Chrome reports "NNN.0.0.0" — any rule that parses digits to gate
 * a feature is wrong the day it ships.
 *
 * 铁律：只认 token，不认版本号。Android 精简 UA 永远是「Android 10; K」，iOS 26
 * 的 Safari 仍报「iPhone OS 18_6」，Chrome 报「NNN.0.0.0」——
 * 任何靠解析版本数字来放行功能的规则，上线当天就是错的。
 */
import { searchHasPreviewMenuKey } from '@/utils/menu/previewMenu'

export const A2HS_PLATFORM = {
  IOS_SAFARI: 'ios-safari',
  ANDROID_CHROME: 'android-chrome',
  UNSUPPORTED: 'unsupported'
}

// Per-app in-app-browser tokens. These come FIRST because the classic
// "iOS webview has no Safari/ token" heuristic is decaying fast — Instagram,
// TikTok and Meta's new MetaIAB UA all append a genuine `Safari/604.1` now.
// 各 App 内置浏览器的专属 token。必须先查这一组：老的「iOS webview 没有
// Safari/」启发式正在失效——IG、TikTok、Meta 新版 UA 都补上了真的 Safari/604.1。
const IN_APP_WEBVIEW_TOKENS = [
  /MicroMessenger/i, // WeChat, both platforms. Never appears in a real browser.
  /\bInstagram\b/i,
  /\bFB[A-Z_]+\//, // FBAN/ FBAV/ FB_IAB/ FBBV/ FBDV/ — Facebook + Messenger
  /MetaIAB/i, // 2025+ Meta iOS builds dropped the whole [FBAN/...] block
  /\bFacebook\b/i,
  /\bIABMV\//i, // marks Meta's newer in-app browser engine across FB/IG/Messenger
  /\bLine\/\d/i, // trailing slash + digit is mandatory: bare /line/i matches "Linux", "Online"
  /\bTwitter/i, // still literally "Twitter" post-rebrand: "Twitter for iPhone", "TwitterAndroid"
  /musical_ly|Bytedance/i, // TikTok never writes "TikTok" in its UA
  /\bAppName\/(musical_ly|trill)\b/i, // international TikTok builds
  /Snapchat\//i, // its iOS UA is otherwise almost byte-identical to Mobile Safari
  /LinkedInApp/i, // NOT /LinkedIn/i — the bare brand appears in crawler agents
  /Pinterest\//i
]

// Google's documented Android WebView signal. Scoped structurally to the
// parenthesised platform block: a bare /wv/ collides with device model codes
// and build IDs, so the surrounding "; " and ";)" are load-bearing.
// Google 官方的 Android WebView 标记。必须结构化匹配括号内那段：裸的 /wv/ 会
// 撞上机型代号和 build ID，前后的「; 」「;)」是必需的。
const ANDROID_WEBVIEW = /;\s*wv[;)]/

const IOS_PLATFORM = /iPhone|iPad|iPod/i
const MACINTOSH = /Macintosh/i
const ANDROID = /Android/i
const SAFARI_TOKEN = /Safari\//i
const VERSION_TOKEN = /Version\/\d/i
const CHROME_TOKEN = /Chrome\/\d/i

// Secondary WebView tell: real Chrome for Android never emits a `Version/`
// token, but WebView always ships `Version/4.0`. Catches unbranded hosts that
// dropped the `wv` marker. Same regex as VERSION_TOKEN on purpose — one source
// of truth, two semantic names.
// 次要判据：真 Android Chrome 从不带 Version/，WebView 一定带 Version/4.0。
// 用来兜住丢了 wv 标记的无品牌 webview。与 VERSION_TOKEN 刻意同源，仅语义命名不同。
const ANDROID_WEBVIEW_VERSION_TELL = VERSION_TOKEN

// iOS third-party browsers. All of them are WebKit wrappers that keep the
// `Safari/` suffix, so they must be excluded explicitly and BEFORE the Safari
// fallthrough — otherwise every iOS browser gets labelled Safari.
// iOS 第三方浏览器。它们都是 WebKit 壳、都保留 Safari/ 后缀，必须在 Safari
// 兜底分支之前显式排除，否则所有 iOS 浏览器都会被认成 Safari。
const IOS_THIRD_PARTY_BROWSER = /CriOS\/|FxiOS\/|EdgiOS\/|OPT\/|OPiOS\/|YaBrowser\//i

// Brand tokens inside the Android Chromium family. Every one of these also
// carries `Chrome/` and `Safari/` (EdgA/ even sits AFTER `Safari/537.36`), so
// brand detection never anchors to the end of the string. Firefox is NOT in
// the family — Gecko engine, no `Chrome/` token, no `beforeinstallprompt` —
// it falls out at the Chrome-token gate.
// Android Chromium 系的品牌 token。它们全都带 Chrome/ 和 Safari/（EdgA/ 还排
// 在 Safari/537.36 之后），品牌匹配不能用 $ 锚定。Firefox 不属于这个家族——
// Gecko 内核、没有 Chrome/ token、不发 beforeinstallprompt，在 Chrome token
// 这关就被挡掉。
const ANDROID_BROWSER_BRAND_TOKENS = [
  { brand: 'samsung', token: /SamsungBrowser\//i },
  { brand: 'edge', token: /EdgA\//i },
  { brand: 'opera', token: /OPR\//i },
  { brand: 'other', token: /YaBrowser\/|MiuiBrowser\/|HuaweiBrowser\/|HeyTapBrowser\/|VivoBrowser\/|UCBrowser\/|QQBrowser\//i }
]

/** Android Chromium-family browser brands. Android Chromium 系浏览器品牌。 */
export const ANDROID_BROWSER_BRAND = {
  CHROME: 'chrome',
  SAMSUNG: 'samsung',
  EDGE: 'edge',
  OPERA: 'opera',
  OTHER: 'other'
}

/** Coerce anything into a usable UA string; non-strings and blanks become ''. */
function normalizeUserAgent(userAgent) {
  if (typeof userAgent !== 'string') return ''
  return userAgent.trim()
}

/**
 * True when the page is running inside an app's built-in browser (WeChat,
 * Instagram, Facebook / Messenger, LINE, X, TikTok, Snapchat, LinkedIn,
 * Pinterest, or a generic Android WebView / iOS WKWebView).
 *
 * None of these can add anything to the home screen, so the coaching popup
 * must stay hidden there — showing it would teach the customer to do something
 * that is literally impossible in that app.
 *
 * 判断页面是否跑在 App 内置浏览器里（微信 / IG / FB / Messenger / LINE / X /
 * TikTok / Snapchat / LinkedIn / Pinterest，以及通用 Android WebView、
 * iOS WKWebView）。这些环境都加不了桌面图标，必须不弹窗，否则等于教顾客做一件
 * 做不到的事。
 *
 * Known blind spot: Telegram, Chrome Custom Tabs and SFSafariViewController
 * pass through the plain system UA with no app token at all — they are
 * undetectable here. We accept that small false-positive rate rather than
 * over-suppressing everyone else.
 * 已知盲区：Telegram、Chrome Custom Tabs、SFSafariViewController 透传系统 UA，
 * 没有任何 App token，这里检测不到。宁可少量误弹，也不过度抑制。
 *
 * @param {string} userAgent
 * @returns {boolean}
 */
export function isInAppWebview(userAgent) {
  const ua = normalizeUserAgent(userAgent)
  if (!ua) return false

  if (IN_APP_WEBVIEW_TOKENS.some(token => token.test(ua))) return true

  if (ANDROID.test(ua)) {
    if (ANDROID_WEBVIEW.test(ua)) return true
    // Chrome/ + Version/ on Android only ever happens inside a WebView.
    if (CHROME_TOKEN.test(ua) && ANDROID_WEBVIEW_VERSION_TELL.test(ua)) return true
    return false
  }

  // Backstop for unbranded iOS WKWebView hosts: every legitimate iOS browser
  // terminates with `Safari/604.1` or `Safari/605.1.15`.
  // 无品牌 iOS WKWebView 兜底：所有正经 iOS 浏览器都以 Safari/6xx 结尾。
  if (IOS_PLATFORM.test(ua) && !SAFARI_TOKEN.test(ua)) return true

  return false
}

/**
 * iPadOS 13+ sends a Macintosh UA by default — byte-identical to macOS Safari,
 * with no `iPad` and no `Mobile` token. There is no UA-only fix, so the caller
 * may hand us `navigator.maxTouchPoints` to recover the iPad. Without it we
 * simply cannot tell an iPad from a Mac, and default to "not iPad".
 *
 * iPadOS 13+ 默认发桌面版 Macintosh UA，和 macOS Safari 一模一样，既没有 iPad
 * 也没有 Mobile。纯 UA 无解，所以允许调用方传入 navigator.maxTouchPoints 把
 * iPad 认回来；不传就无法区分 iPad 和 Mac，默认按「不是 iPad」处理。
 */
function isIpadInDesktopMode(ua, maxTouchPoints) {
  return MACINTOSH.test(ua) && maxTouchPoints > 1
}

/**
 * Real Mobile Safari, positively identified: an iOS platform token (or an
 * iPad hiding behind the Macintosh UA), `Safari/` AND `Version/`, with every
 * third-party iOS browser excluded first.
 *
 * `Version/` alone is not a clean discriminator (EdgiOS has it, Android
 * WebView emits `Version/4.0`), and `Safari` alone matches every Chromium
 * Android UA — the combination plus the exclusion list is what makes it work.
 */
function isIosSafari(ua, maxTouchPoints) {
  if (IOS_THIRD_PARTY_BROWSER.test(ua)) return false
  if (!IOS_PLATFORM.test(ua) && !isIpadInDesktopMode(ua, maxTouchPoints)) return false
  return SAFARI_TOKEN.test(ua) && VERSION_TOKEN.test(ua)
}

/**
 * Android Chromium family (Chrome, Samsung Internet, Edge, Opera, Miui & co):
 * `Chrome/` token, not a WebView. Product call 2026-07-28 — CAPABILITY-gated
 * release: the whole family is admitted because the one-tap button only ever
 * renders after `beforeinstallprompt` is actually captured, so a member that
 * never fires the event simply never shows a dead button (fail-closed), and
 * the guide fallback words itself per brand (detectAndroidBrowserBrand).
 * Android Chromium 系（Chrome、三星、Edge、Opera、Miui 等）：带 Chrome/ token
 * 且不是 WebView。产品拍板 2026-07-28——按「能力」放行：整个家族放进来，因为
 * 一键按钮只在真正捕获 beforeinstallprompt 后才渲染，不发事件的成员永远不会
 * 出现死按钮（fail-closed）；引导兜底按品牌给措辞（detectAndroidBrowserBrand）。
 */
function isAndroidChromiumFamily(ua) {
  if (!ANDROID.test(ua)) return false
  return CHROME_TOKEN.test(ua)
}

/**
 * Which Chromium-family browser is this? Drives the guide wording & arrow
 * position (menu entries live in different corners per brand); analytics use
 * it to segment exposures. Only meaningful when the platform detected as
 * ANDROID_CHROME; returns CHROME for a bare Chrome UA.
 * 判定是 Chromium 系里的哪家浏览器：决定引导措辞与箭头指向（各家菜单入口位置
 * 不同），埋点用它分维度。仅在平台判定为 ANDROID_CHROME 时有意义；
 * 纯 Chrome UA 返回 CHROME。
 *
 * @param {string} userAgent
 * @returns {string} one of the ANDROID_BROWSER_BRAND values
 */
export function detectAndroidBrowserBrand(userAgent) {
  const ua = normalizeUserAgent(userAgent)
  for (const { brand, token } of ANDROID_BROWSER_BRAND_TOKENS) {
    if (token.test(ua)) return brand
  }
  return ANDROID_BROWSER_BRAND.CHROME
}

/**
 * Classify the browsing environment for the add-to-home-screen coaching popup.
 * 判断当前环境属于哪一类「保存到桌面」场景。
 *
 * SCOPE (updated 2026-07-28): the whole Android Chromium family returns
 * ANDROID_CHROME (capability-gated, see isAndroidChromiumFamily). iOS
 * third-party browsers (CriOS / FxiOS / EdgiOS) remain UNSUPPORTED — since
 * iOS 16.4 they CAN add to the home screen, but the share entry sits
 * elsewhere and Safari-shaped instructions would point at a button that is
 * not there; widen once phase-6 analytics say it is worth it.
 *
 * 范围（2026-07-28 更新）：Android Chromium 全家族返回 ANDROID_CHROME（按能力
 * 放行，见 isAndroidChromiumFamily）。iOS 第三方浏览器（CriOS / FxiOS /
 * EdgiOS）维持 UNSUPPORTED——iOS 16.4+ 它们能添加，但分享入口位置不同，照
 * Safari 引导会指向不存在的按钮；等阶段六埋点数据说值得了再放宽。
 *
 * @param {string} userAgent
 * @param {Object} [options]
 * @param {number} [options.maxTouchPoints=0] - navigator.maxTouchPoints, only
 *   used to recover an iPad hiding behind the Macintosh UA / 仅用于把伪装成
 *   Macintosh 的 iPad 认回来
 * @returns {string} one of the A2HS_PLATFORM values
 */
export function detectA2hsPlatform(userAgent, { maxTouchPoints = 0 } = {}) {
  const ua = normalizeUserAgent(userAgent)
  if (!ua) return A2HS_PLATFORM.UNSUPPORTED
  if (isInAppWebview(ua)) return A2HS_PLATFORM.UNSUPPORTED
  if (isIosSafari(ua, maxTouchPoints)) return A2HS_PLATFORM.IOS_SAFARI
  if (isAndroidChromiumFamily(ua)) return A2HS_PLATFORM.ANDROID_CHROME
  return A2HS_PLATFORM.UNSUPPORTED
}

/**
 * The single gate the popup asks before it may ever appear.
 * 弹窗唯一要问的那个开关。
 *
 * False when we are inside an in-app browser, on an unsupported platform, or
 * already opened from a home-screen icon in standalone mode (defensive — this
 * feature deliberately never sets standalone, but if that ever changes the
 * popup must not reappear inside the installed app).
 *
 * 以下任一成立即 false：内置 webview、平台不支持、已经是 standalone 打开
 * （防御性检查——本功能刻意不做 standalone，但万一将来改了，弹窗不能在已保存的
 * 图标里再冒出来）。
 *
 * @param {Object} [env]
 * @param {string} [env.userAgent]
 * @param {boolean} [env.isStandalone]
 * @param {boolean} [env.isTopWindow=true] - false inside an iframe (pc.html
 *   desktop wrapper, third-party embeds). Chrome only honours a manifest in
 *   the top-level document, and iOS would save the wrapper URL — both wrong.
 *   iframe 里（pc.html 桌面壳、第三方内嵌）一律不弹：Chrome 只认顶层文档的
 *   manifest，iOS 存下来的会是壳的地址。
 * @param {number} [env.maxTouchPoints=0]
 * @param {boolean} [env.isPreviewPage=false] - the address carries a
 *   `previewMenu` key (multi-version menu preview, B6 U-B6-3, owner D98②):
 *   never prompt — the saved icon would freeze the code in, and the backend
 *   refuses every order that carries it.
 *   地址带 previewMenu 键（菜单预览页）一律不弹：码会被冻进书签，后端对带码的单一律拒。
 * @returns {boolean}
 */
export function canPromptAddToHomeScreen({ userAgent, isStandalone, isTopWindow = true, maxTouchPoints = 0, isPreviewPage = false } = {}) {
  if (isStandalone) return false
  if (isTopWindow === false) return false
  if (isPreviewPage) return false
  if (isInAppWebview(userAgent)) return false
  return detectA2hsPlatform(userAgent, { maxTouchPoints }) !== A2HS_PLATFORM.UNSUPPORTED
}

/**
 * Read the three inputs above off the live browser. The ONLY impure function
 * here; every branch tolerates a missing navigator / window / matchMedia.
 * 从当前浏览器读上面三个入参。本文件唯一的非纯函数，navigator / window /
 * matchMedia 缺失时都不抛。
 *
 * @returns {{ userAgent: string, isStandalone: boolean, isTopWindow: boolean, maxTouchPoints: number, isPreviewPage: boolean }}
 */
export function readA2hsEnvironment() {
  const nav = typeof navigator === 'undefined' ? null : navigator
  const win = typeof window === 'undefined' ? null : window

  let isStandalone = nav ? nav.standalone === true : false
  if (!isStandalone && win && typeof win.matchMedia === 'function') {
    try {
      isStandalone = win.matchMedia('(display-mode: standalone)').matches === true
    } catch (e) {
      isStandalone = false
    }
  }

  let isTopWindow = true
  if (win) {
    try {
      isTopWindow = win.self === win.top
    } catch (e) {
      isTopWindow = false // 访问 top 抛异常 = 在跨域 iframe 里
    }
  }

  return {
    userAgent: nav && typeof nav.userAgent === 'string' ? nav.userAgent : '',
    isStandalone,
    isTopWindow,
    maxTouchPoints: nav && typeof nav.maxTouchPoints === 'number' ? nav.maxTouchPoints : 0,
    isPreviewPage: !!(win && win.location) && searchHasPreviewMenuKey(win.location.search)
  }
}

/**
 * Is this an iPad (including the Macintosh-UA desktop mode)? The guide popup
 * needs this: iPad Safari puts the Share button in the TOP toolbar, iPhone at
 * the bottom — pointing at the wrong edge coaches a button that isn't there.
 * 是否 iPad（含伪装 Macintosh UA 的桌面模式）。引导弹窗需要区分：iPad 的分享
 * 按钮在顶部工具栏，iPhone 在底部，指错方向等于指向不存在的按钮。
 */
export function isIpadLikeDevice(userAgent, { maxTouchPoints = 0 } = {}) {
  const ua = normalizeUserAgent(userAgent)
  if (!ua) return false
  if (/iPad/i.test(ua)) return true
  return isIpadInDesktopMode(ua, maxTouchPoints)
}

/** Convenience wrapper: canPromptAddToHomeScreen() against the live browser. */
export function canPromptAddToHomeScreenHere() {
  return canPromptAddToHomeScreen(readA2hsEnvironment())
}
